import React from 'react';
import PropTypes from 'prop-types';
import { curry } from 'lodash';
import { EventBus } from '@fiverr-private/futile/lib/event/EventBus';
import reportToBigQuery from './bigQueryReporter';
import { sendMixPanelEvent } from './mixpanel';
import { CLICKED_DOWNLOAD_FILES } from './mixpanel/mixpanelEvents';

const bytesToSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) { return '0 Bytes'; }

    const sizesIndex = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
    return `${Math.round(bytes / Math.pow(1024, sizesIndex), 2)} ${sizes[sizesIndex]}`;
};

const isTouchDevice = () => (
    !!(typeof window !== 'undefined' && ('ontouchstart' in window || (window.DocumentTouch && typeof document !== 'undefined' && document instanceof window.DocumentTouch))) ||
    !!(typeof navigator !== 'undefined' && (navigator.maxTouchPoints || navigator.msMaxTouchPoints))
);

export const collaborationEventBus = new EventBus();

const keyCode = {
    ENTER: 13,
    SPACE_BAR: 32,
    ARROW_LEFT: 37,
    ARROW_UP: 38,
    ARROW_RIGHT: 39,
    ARROW_DOWN: 40,
    ESCAPE: 27
};

const previewType = {
    IMAGE: 'image',
    VIDEO: 'video',
    AUDIO: 'audio',
    TEXT: 'text',
    PDF: 'pdf',
    ARCHIVE: 'archive',
    EDITABLE: 'editable',
    UNKNOWN: ''
};

const previewState = {
    READY: 'ready'
};

const EXTERNAL_GROUP_NAME = 'collaborators';
const SELLER_GROUP_NAME = 'seller';

const SHARE_REQUEST = 'share-request';

const IPAD_MIN_WIDTH_THRESHOLD = 768;
const PC_MIN_WIDTH_THRESHOLD = 1024;

export function downloadLink(downloadUrl, reason, type) {
    reportToBigQuery({
        group: 'attachements',
        type: 'clicked_file_download'
    });

    sendMixPanelEvent({
        eventName: CLICKED_DOWNLOAD_FILES,
        eventData: {
            'Source Component': reason,
            'File Type': type,
            'Attachment Type': 'Order Message',
            'Is Source File': false
        }
    });

    const link = document.createElement('a');
    document.body.appendChild(link);
    link.download = '';
    link.href = downloadUrl;
    link.click();
    if (link.parentNode) {
        link.parentNode.removeChild(link);
    }
}

const FILE_EXTENSION_REGEX = /.+\.(.+)/;

function getLastSeenNormalized(lastSeen) {
    const monthYearDay = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };

    return new Intl.DateTimeFormat('en', monthYearDay).format(lastSeen);
}

function secToTimestamp(time) {
    let minutes = Math.floor(time/60);
    if (minutes < 10){
        minutes = `0${minutes}`;
    }
    let seconds = Math.floor(time - (60 * minutes));
    if (seconds < 10){
        seconds = `0${seconds}`;
    }
    return `${minutes}:${seconds}`;
}

const withContext = curry((Context, mapContextToProps, Component) =>
    (props) => ( // eslint-disable-line react/display-name
        <Context.Consumer>
            {(context) => <Component {...props} {...mapContextToProps(context)}/>}
        </Context.Consumer>
    ));

const RenderIf = ({ condition, children }) => {
    if (condition) {
        return (
            <div>{children}</div>
        );
    }

    return null;
};

RenderIf.propTypes = {
    condition: PropTypes.bool,
    children: PropTypes.any
};

export {
    bytesToSize,
    isTouchDevice,
    keyCode,
    previewType,
    previewState,
    FILE_EXTENSION_REGEX,
    IPAD_MIN_WIDTH_THRESHOLD,
    PC_MIN_WIDTH_THRESHOLD,
    secToTimestamp,
    withContext,
    getLastSeenNormalized,
    EXTERNAL_GROUP_NAME,
    SELLER_GROUP_NAME,
    SHARE_REQUEST,
    RenderIf
};
